/**
 * Focus handling for a ui.bottom_sheet_start sheet with manage_focus: focus on the title at open,
 * the page behind made inert, and focus back to return_focus_expr (or the element focused before open)
 * at close.
 */

import { trapTabFocus } from './focus-trap.js';
import { inertOutside } from './inert-outside.js';

// Open sheets by id, with the undo of the inert page and the element to focus at close.
const openSheets = new Map();

// x-show with a transition shows the panel a frame or more after the state change, and a hidden
// element cannot take focus.
const TITLE_FOCUS_FRAMES = 10;

// The keyup of an Escape that closed a sheet must not reach the return target: the city
// autocomplete blurs its field on an Escape keyup. A keyup lost to another window ends the press.
let escapeHeld = false;
let swallowEscapeKeyup = false;
window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') escapeHeld = true;
}, true);
window.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape') return;
    escapeHeld = false;
    if (swallowEscapeKeyup) {
        swallowEscapeKeyup = false;
        event.stopPropagation();
    }
}, true);
window.addEventListener('blur', () => { escapeHeld = false; });

function focusTitleWhenShown(id, framesLeft) {
    if (!openSheets.has(id)) return;
    const title = document.getElementById(`${id}-title`);
    if (title && title.getClientRects().length > 0) {
        title.focus({ preventScroll: true });
    } else if (framesLeft > 0) {
        requestAnimationFrame(() => focusTitleWhenShown(id, framesLeft - 1));
    }
}

// A hidden element ignores focus().
function isShown(el) {
    return !!el && el !== document.body && el.getClientRects().length > 0;
}

/**
 * Open or close one named sheet's focus handling, at most once per sheet. The sheet's x-effect
 * re-runs on any change its show expression reads, as the scroll lock does.
 *
 * @param {string} id sheet id, unique per page. The title carries `${id}-title`.
 * @param {boolean} open
 * @param {HTMLElement} sheet the sheet root
 * @param {string|null} returnFocus selector of the element to focus at close. A hidden match falls back to
 *   the element focused before open.
 */
export function syncSheetFocus(id, open, sheet, returnFocus) {
    if (open) {
        if (openSheets.has(id) || !sheet) return;
        openSheets.set(id, { restoreInert: inertOutside(sheet), opener: document.activeElement });
        focusTitleWhenShown(id, TITLE_FOCUS_FRAMES);
        return;
    }
    const state = openSheets.get(id);
    if (!state) return;
    openSheets.delete(id);
    state.restoreInert();
    const active = document.activeElement;
    if (active && active !== document.body && !sheet.contains(active)) return;
    const target = [returnFocus ? document.querySelector(returnFocus) : null, state.opener].find(isShown);
    if (!target) return;
    target.focus({ preventScroll: true });
    swallowEscapeKeyup = escapeHeld;
}

// The bottom sheet macro runs from Alpine attributes, which cannot import.
window.sheetFocus = { sync: syncSheetFocus, trap: trapTabFocus };
